/**
 * Whether the browser has agreed not to evict this origin's storage.
 *
 * "No ball is ever lost" is a claim about IndexedDB, and IndexedDB is only as durable as the
 * browser's promise about it. Safari can clear a web app's data after about a week of not
 * being opened unless storage is persistent, and an unsent innings is exactly what sits in
 * that storage between a match and the next one. So the app asks, and logs a refusal.
 *
 * Deliberately not shown to the scorer: they cannot act on it mid-match, and in development —
 * where nothing asks — it would be wrong every time.
 */

/** The slice of `navigator.storage` this needs. An interface so tests need no browser. */
export interface StorageLike {
  persisted(): Promise<boolean>;
  persist(): Promise<boolean>;
}

/** `unsupported` is not `denied`: nothing was refused, there was nobody to ask. */
export type Persistence = "granted" | "denied" | "unsupported";

export const platformStorage = (): StorageLike | undefined =>
  typeof navigator !== "undefined" && "storage" in navigator && "persist" in navigator.storage
    ? navigator.storage
    : undefined;

/** Ask for persistence if it has not been granted, and say what the browser answered. */
export async function ensurePersistence(
  storage: StorageLike | undefined = platformStorage(),
): Promise<Persistence> {
  if (storage === undefined) return "unsupported";
  try {
    if (await storage.persisted()) return "granted";
    return (await storage.persist()) ? "granted" : "denied";
  } catch {
    return "unsupported";
  }
}
